import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  List,
  ListItem,
  ListItemText,
  TextField,
  Button,
  IconButton,
  Divider,
  Alert,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { ModuleOutline } from '../../services/lessonService';

interface ModuleOutlineViewerProps {
  outline: ModuleOutline | null;
  onSave?: (updatedOutline: ModuleOutline) => void;
  editable?: boolean;
}

export const ModuleOutlineViewer: React.FC<ModuleOutlineViewerProps> = ({
  outline,
  onSave,
  editable = true,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editedOutline, setEditedOutline] = useState<ModuleOutline | null>(outline);

  if (!outline) {
    return (
      <Alert severity="info">
        No module outline generated yet. Generate an outline to see the AI-powered structure.
      </Alert>
    );
  }

  const handleEdit = () => {
    setEditedOutline(JSON.parse(JSON.stringify(outline))); // Deep copy
    setIsEditing(true);
  };

  const handleCancel = () => {
    setEditedOutline(outline);
    setIsEditing(false);
  };

  const handleSave = () => {
    if (editedOutline && onSave) {
      onSave(editedOutline);
    }
    setIsEditing(false);
  };

  const currentOutline = isEditing ? editedOutline : outline;

  if (!currentOutline) return null;

  // Helper functions for editing
  const updateField = (path: string[], value: any) => {
    if (!editedOutline) return;
    const newOutline = JSON.parse(JSON.stringify(editedOutline));
    let current: any = newOutline;
    for (let i = 0; i < path.length - 1; i++) {
      current = current[path[i]];
    }
    current[path[path.length - 1]] = value;
    setEditedOutline(newOutline);
  };

  const addArrayItem = (path: string[], defaultValue = '') => {
    if (!editedOutline) return;
    const newOutline = JSON.parse(JSON.stringify(editedOutline));
    let current: any = newOutline;
    for (const key of path) {
      current = current[key];
    }
    current.push(defaultValue);
    setEditedOutline(newOutline);
  };

  const removeArrayItem = (path: string[], index: number) => {
    if (!editedOutline) return;
    const newOutline = JSON.parse(JSON.stringify(editedOutline));
    let current: any = newOutline;
    for (const key of path) {
      current = current[key];
    }
    current.splice(index, 1);
    setEditedOutline(newOutline);
  };

  const updateArrayItem = (path: string[], index: number, value: string) => {
    if (!editedOutline) return;
    const newOutline = JSON.parse(JSON.stringify(editedOutline));
    let current: any = newOutline;
    for (const key of path) {
      current = current[key];
    }
    current[index] = value;
    setEditedOutline(newOutline);
  };

  return (
    <Box>
      {/* Header with Edit Controls */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5">Module Outline</Typography>
        {editable && (
          <Box sx={{ display: 'flex', gap: 1 }}>
            {isEditing ? (
              <>
                <Button
                  startIcon={<CancelIcon />}
                  onClick={handleCancel}
                  variant="outlined"
                  size="small"
                >
                  Cancel
                </Button>
                <Button
                  startIcon={<SaveIcon />}
                  onClick={handleSave}
                  variant="contained"
                  size="small"
                  color="primary"
                >
                  Save Changes
                </Button>
              </>
            ) : (
              <Button startIcon={<EditIcon />} onClick={handleEdit} variant="outlined" size="small">
                Edit Outline
              </Button>
            )}
          </Box>
        )}
      </Box>

      {/* Module Overview */}
      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">📋 Module Overview</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Description
              </Typography>
              {isEditing ? (
                <TextField
                  fullWidth
                  multiline
                  rows={2}
                  value={currentOutline.module_overview.description}
                  onChange={(e) => updateField(['module_overview', 'description'], e.target.value)}
                />
              ) : (
                <Typography variant="body2">
                  {currentOutline.module_overview.description}
                </Typography>
              )}
            </Box>

            <Box>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Why This Matters
              </Typography>
              {isEditing ? (
                <TextField
                  fullWidth
                  multiline
                  rows={2}
                  value={currentOutline.module_overview.importance}
                  onChange={(e) => updateField(['module_overview', 'importance'], e.target.value)}
                />
              ) : (
                <Typography variant="body2">{currentOutline.module_overview.importance}</Typography>
              )}
            </Box>

            <Box>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Essential Questions
              </Typography>
              <List dense>
                {currentOutline.module_overview.essential_questions.map((question, index) => (
                  <ListItem key={index}>
                    {isEditing ? (
                      <Box sx={{ display: 'flex', gap: 1, width: '100%', alignItems: 'center' }}>
                        <TextField
                          fullWidth
                          size="small"
                          value={question}
                          onChange={(e) =>
                            updateArrayItem(
                              ['module_overview', 'essential_questions'],
                              index,
                              e.target.value,
                            )
                          }
                        />
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() =>
                            removeArrayItem(['module_overview', 'essential_questions'], index)
                          }
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    ) : (
                      <ListItemText primary={`• ${question}`} />
                    )}
                  </ListItem>
                ))}
                {isEditing && (
                  <ListItem>
                    <Button
                      size="small"
                      startIcon={<AddIcon />}
                      onClick={() => addArrayItem(['module_overview', 'essential_questions'], '')}
                    >
                      Add Question
                    </Button>
                  </ListItem>
                )}
              </List>
            </Box>
          </Box>
        </AccordionDetails>
      </Accordion>

      {/* Learning Objectives */}
      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">🎯 Learning Objectives</Typography>
          <Chip
            label={currentOutline.learning_objectives.length}
            size="small"
            sx={{ ml: 2 }}
            color="primary"
          />
        </AccordionSummary>
        <AccordionDetails>
          <List dense>
            {currentOutline.learning_objectives.map((objective, index) => (
              <ListItem key={index}>
                {isEditing ? (
                  <Box sx={{ display: 'flex', gap: 1, width: '100%', alignItems: 'center' }}>
                    <TextField
                      fullWidth
                      size="small"
                      value={objective}
                      onChange={(e) =>
                        updateArrayItem(['learning_objectives'], index, e.target.value)
                      }
                    />
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => removeArrayItem(['learning_objectives'], index)}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ) : (
                  <ListItemText primary={`${index + 1}. ${objective}`} />
                )}
              </ListItem>
            ))}
            {isEditing && (
              <ListItem>
                <Button
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => addArrayItem(['learning_objectives'], '')}
                >
                  Add Objective
                </Button>
              </ListItem>
            )}
          </List>
        </AccordionDetails>
      </Accordion>

      {/* Prerequisite Knowledge */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">📚 Prerequisite Knowledge</Typography>
          <Chip label={currentOutline.prerequisite_knowledge.length} size="small" sx={{ ml: 2 }} />
        </AccordionSummary>
        <AccordionDetails>
          <List dense>
            {currentOutline.prerequisite_knowledge.map((prereq, index) => (
              <ListItem key={index}>
                {isEditing ? (
                  <Box sx={{ display: 'flex', gap: 1, width: '100%', alignItems: 'center' }}>
                    <TextField
                      fullWidth
                      size="small"
                      value={prereq}
                      onChange={(e) =>
                        updateArrayItem(['prerequisite_knowledge'], index, e.target.value)
                      }
                    />
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => removeArrayItem(['prerequisite_knowledge'], index)}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ) : (
                  <ListItemText primary={`• ${prereq}`} />
                )}
              </ListItem>
            ))}
            {isEditing && (
              <ListItem>
                <Button
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => addArrayItem(['prerequisite_knowledge'], '')}
                >
                  Add Prerequisite
                </Button>
              </ListItem>
            )}
          </List>
        </AccordionDetails>
      </Accordion>

      {/* Key Vocabulary */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">📖 Key Vocabulary</Typography>
          <Chip label={currentOutline.key_vocabulary.length} size="small" sx={{ ml: 2 }} />
        </AccordionSummary>
        <AccordionDetails>
          {isEditing ? (
            <List dense>
              {currentOutline.key_vocabulary.map((term, index) => (
                <ListItem key={index}>
                  <Box sx={{ display: 'flex', gap: 1, width: '100%', alignItems: 'center' }}>
                    <TextField
                      fullWidth
                      size="small"
                      value={term}
                      onChange={(e) => updateArrayItem(['key_vocabulary'], index, e.target.value)}
                    />
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => removeArrayItem(['key_vocabulary'], index)}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </ListItem>
              ))}
              <ListItem>
                <Button
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => addArrayItem(['key_vocabulary'], '')}
                >
                  Add Term
                </Button>
              </ListItem>
            </List>
          ) : (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {currentOutline.key_vocabulary.map((term, index) => (
                <Chip key={index} label={term} size="small" variant="outlined" />
              ))}
            </Box>
          )}
        </AccordionDetails>
      </Accordion>

      {/* Assessment Overview */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">✅ Assessment Overview</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Formative Strategies
              </Typography>
              <List dense>
                {currentOutline.assessment_overview.formative_strategies.map((strategy, index) => (
                  <ListItem key={index}>
                    {isEditing ? (
                      <Box sx={{ display: 'flex', gap: 1, width: '100%', alignItems: 'center' }}>
                        <TextField
                          fullWidth
                          size="small"
                          value={strategy}
                          onChange={(e) =>
                            updateArrayItem(
                              ['assessment_overview', 'formative_strategies'],
                              index,
                              e.target.value,
                            )
                          }
                        />
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() =>
                            removeArrayItem(['assessment_overview', 'formative_strategies'], index)
                          }
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    ) : (
                      <ListItemText primary={`• ${strategy}`} />
                    )}
                  </ListItem>
                ))}
                {isEditing && (
                  <ListItem>
                    <Button
                      size="small"
                      startIcon={<AddIcon />}
                      onClick={() =>
                        addArrayItem(['assessment_overview', 'formative_strategies'], '')
                      }
                    >
                      Add Strategy
                    </Button>
                  </ListItem>
                )}
              </List>
            </Box>

            <Divider />

            <Box>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Summative Approach
              </Typography>
              {isEditing ? (
                <TextField
                  fullWidth
                  multiline
                  rows={2}
                  value={currentOutline.assessment_overview.summative_approach}
                  onChange={(e) =>
                    updateField(['assessment_overview', 'summative_approach'], e.target.value)
                  }
                />
              ) : (
                <Typography variant="body2">
                  {currentOutline.assessment_overview.summative_approach}
                </Typography>
              )}
            </Box>

            <Divider />

            <Box>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Major Projects
              </Typography>
              <List dense>
                {currentOutline.assessment_overview.major_projects.map((project, index) => (
                  <ListItem key={index}>
                    {isEditing ? (
                      <Box sx={{ display: 'flex', gap: 1, width: '100%', alignItems: 'center' }}>
                        <TextField
                          fullWidth
                          size="small"
                          value={project}
                          onChange={(e) =>
                            updateArrayItem(
                              ['assessment_overview', 'major_projects'],
                              index,
                              e.target.value,
                            )
                          }
                        />
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() =>
                            removeArrayItem(['assessment_overview', 'major_projects'], index)
                          }
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    ) : (
                      <ListItemText primary={`• ${project}`} />
                    )}
                  </ListItem>
                ))}
                {isEditing && (
                  <ListItem>
                    <Button
                      size="small"
                      startIcon={<AddIcon />}
                      onClick={() => addArrayItem(['assessment_overview', 'major_projects'], '')}
                    >
                      Add Project
                    </Button>
                  </ListItem>
                )}
              </List>
            </Box>
          </Box>
        </AccordionDetails>
      </Accordion>

      {/* Differentiation */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">🎨 Differentiation Strategies</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Support for Struggling Learners
              </Typography>
              {isEditing ? (
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  value={currentOutline.differentiation.support_strategies}
                  onChange={(e) =>
                    updateField(['differentiation', 'support_strategies'], e.target.value)
                  }
                />
              ) : (
                <Typography variant="body2">
                  {currentOutline.differentiation.support_strategies}
                </Typography>
              )}
            </Box>

            <Divider />

            <Box>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Extensions for Advanced Students
              </Typography>
              {isEditing ? (
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  value={currentOutline.differentiation.extension_strategies}
                  onChange={(e) =>
                    updateField(['differentiation', 'extension_strategies'], e.target.value)
                  }
                />
              ) : (
                <Typography variant="body2">
                  {currentOutline.differentiation.extension_strategies}
                </Typography>
              )}
            </Box>
          </Box>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};
